import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, Redirect, useParams } from "react-router-dom";
import Iframe from "react-iframe";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import SelectAllClass from "../ChildComponent/SelectAllClass";
import "../css/Admin_css/admin-customize.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const STUDENT_DATA_FIELDS = [
  { key: "sr", label: "Sr." },
  { key: "student_id", label: "Student id" },
  { key: "student_name", label: "Student Name" },
  { key: "password", label: "Password" },
  { key: "class", label: "Class" },
  { key: "roll_no", label: "Roll No" },
  { key: "adm_no", label: "Adm No" },
  { key: "state_specific_id", label: "State Specific Id" },
  { key: "student_pen", label: "Student PEN" },
  { key: "student_apaar", label: "Student APAAR" },
  { key: "admission_type", label: "Admission Type" },
  { key: "admission_date", label: "Admission Date" },
  { key: "gender", label: "Gender" },
  { key: "birthday", label: "BirthDay" },
  { key: "religion", label: "Religion" },
  { key: "community", label: "Community" },
  { key: "caste", label: "Caste" },
  { key: "aadhar_no", label: "Aadhar No" },
  { key: "father_name", label: "Father Name" },
  { key: "father_qualification", label: "Father Qualification" },
  { key: "father_occupation", label: "Father Occupation" },
  { key: "mother_name", label: "Mother" },
  { key: "mother_qualification", label: "Mother Qualification" },
  { key: "mother_occupation", label: "Mother Occupation" },
  { key: "contact", label: "Contact" },
  { key: "current_address", label: "Current Address" },
  { key: "permanent_address", label: "Permanent Address" },
  { key: "status", label: "Status" },
];

const DEFAULT_SELECTED_FIELDS = STUDENT_DATA_FIELDS.map((field) => field.key);

const FIELD_STYLES = {
  panel: {
    marginTop: "14px",
    borderRadius: "8px",
    overflow: "hidden",
    border: `2px solid ${COLORS.primary_color_shade2}`,
    boxShadow: `0 1px 4px ${COLORS.primary_color_shade2}55`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "8px",
    padding: "8px 12px",
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade4} 0%, ${COLORS.primary_color_shade5} 100%)`,
  },
  headerTitle: {
    margin: 0,
    fontSize: "11px",
    fontWeight: "800",
    color: "#fff",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  actionBtn: {
    border: "2px solid rgba(255, 255, 255, 0.65)",
    borderRadius: "6px",
    background: "rgba(255, 255, 255, 0.92)",
    padding: "4px 10px",
    fontSize: "11px",
    fontWeight: "700",
    color: COLORS.primary_color_shade8,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  body: {
    background: COLORS.primary_color_shade0,
    padding: "8px 12px",
  },
  fieldCard: {
    padding: "6px 8px",
    borderRadius: "8px",
    border: `2px solid ${COLORS.primary_color_shade3}`,
    background: COLORS.white,
    cursor: "pointer",
    transition: "all 0.2s ease",
    textAlign: "center",
    boxShadow: `0 1px 3px ${COLORS.primary_color_shade2}55`,
    position: "relative",
  },
  fieldCardSelected: {
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade6} 100%)`,
    border: `2px solid ${COLORS.primary_color_shade7}`,
    boxShadow: `0 3px 8px ${COLORS.primary_color_shade4}66`,
  },
  fieldText: {
    fontSize: "11px",
    fontWeight: "700",
    color: COLORS.primary_color_shade8,
    margin: 0,
    lineHeight: "1.25",
  },
  fieldTextSelected: {
    color: COLORS.white,
    fontWeight: "800",
  },
  fieldTick: {
    fontSize: "9px",
    color: COLORS.white,
    flexShrink: 0,
  },
  applyBtn: {
    border: "2px solid rgba(255, 255, 255, 0.85)",
    borderRadius: "6px",
    background: `linear-gradient(135deg, ${COLORS.primary_color_shade7} 0%, ${COLORS.primary_color_shade8} 100%)`,
    padding: "4px 14px",
    fontSize: "11px",
    fontWeight: "800",
    color: "#fff",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 6px rgba(1, 87, 155, 0.35)",
  },
  applyBtnDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
    boxShadow: "none",
  },
};

const PANEL_STYLE = {
  margin: "0 20px 20px",
  padding: "0",
};

const PAGE_TITLE_BAR_STYLE = {
  width: "100%",
  background: `linear-gradient(135deg, ${COLORS.primary_color_shade6} 0%, ${COLORS.primary_color_shade8} 100%)`,
  padding: "16px 24px",
  borderRadius: "8px 8px 0 0",
  boxShadow: `0 2px 4px ${COLORS.primary_color_shade3}`,
  marginTop: 0,
};

const PAGE_TITLE_STYLE = {
  fontSize: "24px",
  fontWeight: "600",
  color: "#fff",
  margin: 0,
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const PANEL_BODY_STYLE = {
  padding: "20px 24px 30px",
};

function FieldSelector({
  fields,
  selectedFields,
  hasPendingChanges,
  onToggleField,
  onSelectAll,
  onDeselectAll,
  onApply,
}) {
  const selectedSet = new Set(selectedFields);

  return (
    <div style={FIELD_STYLES.panel}>
      <div style={FIELD_STYLES.header}>
        <p style={FIELD_STYLES.headerTitle}>
          <i className="fa fa-columns" aria-hidden="true" />
          Table Columns
          <span style={{ opacity: 0.9, fontWeight: "600" }}>
            ({selectedFields.length}/{fields.length})
          </span>
        </p>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          <button type="button" style={FIELD_STYLES.actionBtn} onClick={onSelectAll}>
            Select All
          </button>
          <button type="button" style={FIELD_STYLES.actionBtn} onClick={onDeselectAll}>
            Deselect All
          </button>
          <button
            type="button"
            style={{
              ...FIELD_STYLES.applyBtn,
              ...(hasPendingChanges ? {} : FIELD_STYLES.applyBtnDisabled),
            }}
            onClick={onApply}
            disabled={!hasPendingChanges}
          >
            <i className="fa fa-check" aria-hidden="true" style={{ marginRight: "5px" }} />
            Apply
          </button>
        </div>
      </div>
      <div style={FIELD_STYLES.body}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: "6px",
          }}
        >
          {fields.map((field) => {
            const isSelected = selectedSet.has(field.key);
            return (
              <div
                key={field.key}
                role="button"
                tabIndex={0}
                style={{
                  ...FIELD_STYLES.fieldCard,
                  ...(isSelected ? FIELD_STYLES.fieldCardSelected : {}),
                }}
                onClick={() => onToggleField(field.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onToggleField(field.key);
                  }
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = COLORS.primary_color_shade1;
                    e.currentTarget.style.borderColor = COLORS.primary_color_shade5;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = COLORS.white;
                    e.currentTarget.style.borderColor = COLORS.primary_color_shade3;
                  }
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "5px",
                  }}
                >
                  {isSelected ? (
                    <i
                      className="fa fa-check"
                      aria-hidden="true"
                      style={FIELD_STYLES.fieldTick}
                    />
                  ) : null}
                  <p
                    style={{
                      ...FIELD_STYLES.fieldText,
                      ...(isSelected ? FIELD_STYLES.fieldTextSelected : {}),
                    }}
                  >
                    {field.label}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PageHeader({ onBack }) {
  return (
    <div
      className="content-header"
      style={{ background: COLORS.primary_color_shade5 }}
    >
      <div className="leftside-content-header">
        <ul className="breadcrumbs">
          <li>
            <i
              className="fa fa-home"
              aria-hidden="true"
              style={{ color: COLORS.white }}
            />
            <Link to="/AdminMain/DataCenter" style={{ color: COLORS.white }}>
              Data Center
            </Link>
          </li>
        </ul>
      </div>
      <div className="rightside-content-header">
        <BackButton onClick={onBack} />
      </div>
    </div>
  );
}

const StudentDataPage = memo(
  ({
    pageTitle,
    fileUrl,
    height,
    showPreview,
    showFieldSelector,
    selectedFields,
    hasPendingFieldChanges,
    onToggleField,
    onSelectAllFields,
    onDeselectAllFields,
    onApplyFields,
    onClassSelect,
    onPreviewLoad,
    onBack,
    onRetry,
    error,
  }) => {
    const iframeRef = useRef(null);

    if (error) {
      return (
        <div className="content" style={{ background: COLORS.whitesmoke }}>
          <PageHeader onBack={onBack} />
          <div
            className="alert alert-danger"
            role="alert"
            style={{
              margin: "20px",
              paddingLeft: "30px",
              paddingRight: "30px",
            }}
          >
            <h4 className="alert-heading">Error Loading Data</h4>
            <p>{error}</p>
            <hr />
            <button className="btn btn-primary" onClick={onRetry}>
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="content" style={{ background: COLORS.whitesmoke }}>
        <PageHeader onBack={onBack} />

        <div
          className="animated fadeInUp bg_page_white_rounded box_shadow"
          style={PANEL_STYLE}
        >
          <div style={PAGE_TITLE_BAR_STYLE}>
            <h3 style={PAGE_TITLE_STYLE}>
              <i className="fa fa-users" aria-hidden="true" style={{ color: "#fff" }} />
              {pageTitle || "Student Data"}
            </h3>
          </div>

          <div style={PANEL_BODY_STYLE}>
            <SelectAllClass action={onClassSelect} selected_position="-1" />

            {showFieldSelector ? (
              <FieldSelector
                fields={STUDENT_DATA_FIELDS}
                selectedFields={selectedFields}
                hasPendingChanges={hasPendingFieldChanges}
                onToggleField={onToggleField}
                onSelectAll={onSelectAllFields}
                onDeselectAll={onDeselectAllFields}
                onApply={onApplyFields}
              />
            ) : null}

            {!fileUrl ? (
              <div className="text-center py-5">
                <i className="fa fa-users fa-3x text-muted mb-3" />
                <h5 className="text-muted">No Preview Available</h5>
                <p className="text-muted">
                  Select board, medium and class to load student data.
                </p>
              </div>
            ) : (
              <div className="row" style={{ margin: 0 }}>
                {showPreview ? <MyLoader /> : null}
                <Iframe
                  ref={iframeRef}
                  src={fileUrl}
                  width="100%"
                  height={height}
                  style={{ border: "none", overflow: "hidden" }}
                  id="iFrame1"
                  scrolling="no"
                  onLoad={onPreviewLoad}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

StudentDataPage.displayName = "StudentDataPage";

const StudentData = () => {
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const [fileDirectory, setFileDirectory] = useState("");
  const [fileName, setFileName] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [fetchError, setFetchError] = useState(null);
  const [height, setHeight] = useState("300px");
  const [selectedFields, setSelectedFields] = useState(DEFAULT_SELECTED_FIELDS);
  const [appliedFields, setAppliedFields] = useState(DEFAULT_SELECTED_FIELDS);

  const classParamsRef = useRef(null);
  const { data_id } = useParams();

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/AdminMain/DataCenter";
    }
  }, []);

  const fetchDataCenterDetail = useCallback(async () => {
    if (!data_id) {
      setFetchError("Data id is missing.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setFetchError(null);

      const response = await axios.get(`${API_BASE()}getDataCenterById.php`, {
        params: { data_id },
      });

      if (response.data.api_status === "OK" && response.data.data) {
        const detail = response.data.data;
        setFileDirectory(detail.file_directory || "");
        setFileName(detail.file_name || "");
        setPageTitle(detail.data_caption || "");
      } else {
        const remark =
          response.data.api_remark || "Failed to load data center details.";
        setFetchError(remark);
        myToast.notify_warning(remark);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.api_remark ||
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch data center details";
      setFetchError(errorMessage);
      console.error("Error fetching data center detail:", err);
      myToast.notify_error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [data_id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchDataCenterDetail();
  }, [fetchDataCenterDetail]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.iframeHeight) {
        setHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", handleMessage, false);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const buildPrintableUrl = useCallback(
    (class_id, section_id, session_id, visibleFields) => {
      const schoolCode = SessionManager.getSchoolCode();
      const fieldsParam = encodeURIComponent(visibleFields.join(","));
      return (
        `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR_PRINTABLE}` +
        `${fileDirectory}/${fileName}` +
        `?&class_id=${class_id}&section_id=${section_id}` +
        `&session_id=${session_id}&school_code=${schoolCode}` +
        `&visible_fields=${fieldsParam}`
      );
    },
    [fileDirectory, fileName]
  );

  const updateFileUrl = useCallback(
    (class_id, section_id, session_id, visibleFields = appliedFields) => {
      if (!fileDirectory || !fileName) {
        myToast.notify_warning("Report file details are not available yet.");
        return;
      }

      classParamsRef.current = { class_id, section_id, session_id };
      setFileUrl(buildPrintableUrl(class_id, section_id, session_id, visibleFields));
      setShowPreview(true);
    },
    [buildPrintableUrl, fileDirectory, fileName, appliedFields]
  );

  const hasPendingFieldChanges =
    selectedFields.length !== appliedFields.length ||
    selectedFields.some((key) => !appliedFields.includes(key));

  const handleToggleField = useCallback((fieldKey) => {
    setSelectedFields((prev) => {
      const isSelected = prev.includes(fieldKey);
      if (isSelected && prev.length === 1) {
        myToast.notify_warning("At least one column must remain selected.");
        return prev;
      }
      return isSelected
        ? prev.filter((key) => key !== fieldKey)
        : [...prev, fieldKey];
    });
  }, []);

  const handleSelectAllFields = useCallback(() => {
    setSelectedFields(DEFAULT_SELECTED_FIELDS);
  }, []);

  const handleDeselectAllFields = useCallback(() => {
    setSelectedFields(["sr", "student_name"]);
    myToast.notify_warning("Sr. and Student Name are kept as minimum columns.");
  }, []);

  const handleApplyFields = useCallback(() => {
    if (!hasPendingFieldChanges) {
      return;
    }

    const nextFields = [...selectedFields];
    setAppliedFields(nextFields);

    const params = classParamsRef.current;
    if (!params || !fileDirectory || !fileName) {
      myToast.notify_warning("Select a class to refresh the preview.");
      return;
    }

    setFileUrl(
      buildPrintableUrl(
        params.class_id,
        params.section_id,
        params.session_id,
        nextFields
      )
    );
    setShowPreview(true);
  }, [
    hasPendingFieldChanges,
    selectedFields,
    buildPrintableUrl,
    fileDirectory,
    fileName,
  ]);

  const handlePreviewLoad = useCallback(() => {
    setShowPreview(false);
  }, []);

  const classSelectHandler = useCallback(
    (class_id, section_id, session_id) => {
      updateFileUrl(class_id, section_id, session_id);
    },
    [updateFileUrl]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (loading) {
    return <MyLoader />;
  }

  return (
    <StudentDataPage
      pageTitle={pageTitle}
      fileUrl={fileUrl}
      height={height}
      showPreview={showPreview}
      showFieldSelector={!!fileDirectory && !!fileName}
      selectedFields={selectedFields}
      hasPendingFieldChanges={hasPendingFieldChanges}
      onToggleField={handleToggleField}
      onSelectAllFields={handleSelectAllFields}
      onDeselectAllFields={handleDeselectAllFields}
      onApplyFields={handleApplyFields}
      onClassSelect={classSelectHandler}
      onPreviewLoad={handlePreviewLoad}
      onBack={handleBack}
      onRetry={fetchDataCenterDetail}
      error={fetchError}
    />
  );
};

StudentData.displayName = "StudentData";

export default memo(StudentData);
